<template>
  <div class="product-page">
    <LayoutDashboard>
      <transition name="fadeIn">
        <div v-if="product" class="product-page__container" itemscope itemtype="https://schema.org/Product">
          <Search
            v-model="searchTerm"
            class="product-page__field"
            @submit="searchTerm.length && $router.push({ name: 'marketSearch', query: { q: searchTerm } })"
          />

          <Breadcrumbs :items="breadcrumbs" class="product-page__breadcrumbs" />

          <template v-if="inResponsiveSizes(['xs', 'sm'])">
            <ProductPageMeta class="product-page__meta" :product="product" />
            <ProductPageSlider class="product-page__gallery" :product="product" />
            <transition name="fadeInOut">
              <ProductPageBuy v-show="pinBuyBlock" class="product-buy--pinned" :product="product" />
            </transition>
            <div ref="buyBlock" class="product-page__buy">
              <ProductPageBuy :product="product" />
            </div>
            <ProductPageShare :product="product" :share-title="metaInfo.title" class="product-page__share" />
            <ProductPageSeller :product="product" class="product-page__seller" />
            <ProductPageDelivery
              :product="product"
              :delivery-address="deliveryAddress"
              :pickup-address="pickupAddress"
              class="product-page__delivery"
            />
            <ProductPageSpecs :product="product" class="product-page__specs" />
            <ProductPageTabs
              :product="product"
              :delivery-address="deliveryAddress"
              :pickup-address="pickupAddress"
              class="product-page__tabs"
            />
            <ProductPageQuestions :product="product" class="product-page__questions" />
            <transition name="fadeIn">
              <ProductPageBoughtTogether class="product-page__recommendations product-page__suggested-goods" />
            </transition>
          </template>

          <template v-else>
            <div class="product-page__column product-page__column--1">
              <ProductPageSlider class="product-page__gallery" :product="product" />
              <ProductPageSeller :product="product" class="product-page__seller" />
              <ProductPageTabs
                :product="product"
                :delivery-address="deliveryAddress"
                :pickup-address="pickupAddress"
                class="product-page__tabs"
              />
            </div>

            <div class="product-page__column product-page__column--2">
              <div class="product-page__right">
                <div class="product-page__sub-column">
                  <ProductPageMeta class="product-page__meta" :product="product" />
                  <ProductPageSpecs :product="product" class="product-page__specs" />
                  <ProductPageQuestions :product="product" class="product-page__questions" />
                </div>

                <div class="product-page__sub-column">
                  <ProductPageShare :product="product" :share-title="metaInfo.title" class="product-page__share" />
                  <ProductPageBuy :product="product" class="product-page__buy" />
                  <ProductPageDelivery
                    :product="product"
                    :delivery-address="deliveryAddress"
                    :pickup-address="pickupAddress"
                    class="product-page__delivery"
                  />
                </div>

                <transition name="fadeIn">
                  <ProductPageBoughtTogether class="product-page__recommendations product-page__suggested-goods" />
                </transition>
              </div>
            </div>
          </template>

          <transition name="fadeIn">
            <div v-if="similarProducts.length" class="product-page__recommendations product-page__similar-goods">
              <p class="product-page__recommendations-title">
                {{ $t('Similar goods') }}
              </p>

              <div class="product-page__recommendations-list product-page__recommendations-list--similar">
                <ProductCard
                  v-for="item in similarProducts.slice(
                    0,
                    inResponsiveSizes(['xs']) ? 2 : inResponsiveSizes('sm') ? 3 : 4,
                  )"
                  :key="item.id"
                  :product="item"
                />
              </div>

              <router-link
                :to="{
                  name: 'productsByProduct',
                  params: {
                    productCategory: product.group.routeName,
                    productName: product.product.routeName,
                    scrollTop: true,
                  },
                }"
                class="product-page__recommendations-more"
              >
                <span>{{ $t('See more') }}</span>
                <ChevronRight />
              </router-link>
            </div>
          </transition>

          <transition name="fadeIn">
            <div v-if="popularInCategory.length" class="product-page__recommendations product-page__category-goods">
              <p class="product-page__recommendations-title">
                {{ $t('Popular in category') }}
              </p>

              <div class="product-page__recommendations-list product-page__recommendations-list--in-category">
                <ProductCard
                  v-for="item in popularInCategory.slice(
                    0,
                    inResponsiveSizes(['xs']) ? 2 : inResponsiveSizes('sm') ? 3 : 4,
                  )"
                  :key="item.id"
                  :product="item"
                />
              </div>

              <router-link
                :to="{
                  name: 'productsByCategory',
                  params: {
                    productCategory: product.group.routeName,
                    scrollTop: true,
                  },
                }"
                class="product-page__recommendations-more"
              >
                <span>{{ $t('See more') }}</span>
                <ChevronRight />
              </router-link>
            </div>
          </transition>
        </div>
      </transition>

      <PageNotFound v-if="notFound" />
    </LayoutDashboard>
  </div>
</template>

<script>
import Breadcrumbs from '@/components/common/Breadcrumbs.vue'
import { baseUrl, MARKET_RELATED_ROUTE_NAMES, SELLER_PRODUCT_STATUS } from '@/constants'
import { eventBus } from '@/helpers'
import { RR } from '@/plugins/retailRocket'
import { ECOMMERCE_ACTION_TYPE, Metrics } from '@/utils/metrics'
import { mapGetters, mapState } from 'vuex'
import { filterAvailableForPickupWarehouses } from '@/helpers/cart/filterAvailableForPickupWarehouses'
import ProductCard from '@/components/cards/product/ProductCard.vue'
import inResponsiveSizes from '@/helpers/filters/inResponsiveSizes'
import ProductPageMeta from './components/ProductPageMeta.vue'
import ProductPageSlider from './components/ProductPageSlider.vue'
import ProductPageBuy from './components/ProductPageBuy.vue'
import ProductPageShare from './components/ProductPageShare.vue'
import ProductPageSeller from './components/ProductPageSeller.vue'
import ProductPageDelivery from './components/ProductPageDelivery.vue'
import ProductPageTabs from './components/ProductPageTabs.vue'
import ProductPageQuestions from './components/ProductPageQuestions.vue'
import ProductPageBoughtTogether from './components/ProductPageBoughtTogether.vue'
import ProductPageSpecs from './components/ProductPageSpecs.vue'
import { ChevronRight } from 'lucide-vue'
import PageNotFound from '@/components/common/PageNotFound.vue'
import Search from '@/components/common/Search.vue'

export default {
  name: 'ProductDetailNew',
  components: {
    Breadcrumbs,
    ProductCard,
    ProductPageMeta,
    ProductPageSlider,
    ProductPageBuy,
    ProductPageShare,
    ProductPageSeller,
    ProductPageDelivery,
    ProductPageTabs,
    ProductPageQuestions,
    ProductPageBoughtTogether,
    ProductPageSpecs,
    ChevronRight,
    PageNotFound,
    Search,
  },
  metaInfo() {
    return { ...this.metaInfo }
  },
  data: () => ({
    isLoading: false,
    product: null,
    pinBuyBlock: false,
    buyBlockOffset: null,
    notFound: false,
    searchTerm: '',
  }),
  computed: {
    ...mapState('user', ['currentUser']),
    ...mapState('product', ['groups', 'products']),
    ...mapState('productPage', ['boughtTogether', 'similarProducts', 'popularInCategory']),
    ...mapGetters('auth', ['loggedIn']),
    metaInfo() {
      if (!this.product) {
        return { title: this.$t('market.title') }
      }

      const product = this.product
      const productName = `${product.product.name} ${product.sort.name}`
      const description = `Купите ${productName} из ${product.country.name}, по низкой цене! [мелкий ОПТ] | ${
        product.caliber ? `[Калибр ${product.caliber}] | ` : ''
      }[${product.category.name}] | [Упаковка: ${product.packing.name.toLowerCase()} ${product.weight} ${this.$t(
        `units.weight.${product.wt_unit.name}`,
      )}] | [Доставка до двери!]`

      return {
        title: `${productName} – ${
          product.country.name
        }, ${product.category.name.toLowerCase()}, ${product.packing.name.toLowerCase()} ${this.$t('from {0}', [
          `${product.weight} ${this.$t(`units.weight.${product.wt_unit.name}`)}`,
        ])}`,
        meta: [
          {
            vmid: 'description',
            name: 'description',
            content: description,
          },
          { vmid: 'og:title', property: 'og:title', content: `${productName} – ${product.country.name}` },
          { vmid: 'og:description', property: 'og:description', content: description },
          {
            vmid: 'og:image',
            property: 'og:image',
            content: product.images[0]?.image_medium || '',
          },
          { vmid: 'og:type', property: 'og:type', content: 'product' },
          { vmid: 'og:url', property: 'og:url', content: `${baseUrl}${this.$route.path}` },
        ],
        link: [{ rel: 'canonical', href: `${baseUrl}${this.$route.path}` }],
      }
    },
    breadcrumbs() {
      let items = [{ name: this.$t('market.title'), route: { name: 'market' } }]

      if (this.product) {
        items = [
          ...items,
          {
            name: this.product.group.name,
            route: {
              name: 'productsByCategory',
              params: { productCategory: this.groups.find(g => g.id === this.product.group.id)?.routeName },
            },
          },
          {
            name: this.product.product.name,
            route: {
              name: 'productsByProduct',
              params: {
                productCategory: this.groups.find(g => g.id === this.product.group.id)?.routeName,
                productName: this.products.find(p => p.id === this.product.product.id)?.routeName,
              },
            },
          },
          {
            name: `${this.product.product.name} ${this.product.sort.name}`,
            route: {
              name: 'productDetail',
            },
          },
        ]
      }

      return items
    },
    hasSystemSeller() {
      return this.product.seller.is_system_seller
    },
    pickupAddress() {
      const allPickupWarehouses = filterAvailableForPickupWarehouses(this.hasSystemSeller, [this.product])

      const productWarehouse = this.hasSystemSeller
        ? this.product.warehouse_address
        : this.product.seller.term_condition.warehouse_address

      return allPickupWarehouses.find(w => w.id === productWarehouse?.id)
    },
    deliveryAddress() {
      if (!this.loggedIn) {
        return
      }

      return this.currentUser.profile?.default_store
    },
  },
  watch: {
    product: {
      handler(val) {
        if (val) {
          this.$nextTick(() => {
            this.setupPriceBlockPinning()
          })
        }
      },
      immediate: true,
    },
    '$route.params.productSlug': {
      handler() {
        this.product = null
        this.searchTerm = ''
        this.$store.dispatch('productPage/resetState')
        this.pinBuyBlock = false
        this.loadData()
      },
      immediate: true,
    },
  },
  beforeDestroy() {
    this.$store.dispatch('productPage/resetState')
    window.removeEventListener('scroll', this.handleScroll)
    window.removeEventListener('resize', this.updateBuyBlockOffset)
  },
  methods: {
    inResponsiveSizes,
    async loadData() {
      this.isLoading = true

      this.$store.dispatch('profileProduct/getFilterDataForAllProducts')
      this.$store.dispatch('product/listProductGroups')
      this.$store.dispatch('product/listProducts', { params: { page_size: 1000 } })

      try {
        const productId = this.extractProductId(this.$route.params.productSlug)
        const product = await this.$store.dispatch('profileProduct/loadSellerProduct', { id: productId })

        // const isExpired = new Date(product.expire_at) < new Date()
        const isViewable = [SELLER_PRODUCT_STATUS.publish, SELLER_PRODUCT_STATUS.not_publish].includes(product.status)

        if (
          !isViewable
          //  || isExpired
        ) {
          throw new Error(this.$t('Not found'))
        }

        const withCorrectSeller = {
          ...product,
          seller: product.supplier !== null ? product.supplier : product.seller,
        }

        this.product = withCorrectSeller

        if (MARKET_RELATED_ROUTE_NAMES.includes(this.$route.name)) {
          Metrics.reportEvent('product_detail_view', {
            product_name: `${this.product.product.name} ${this.product.sort?.name}`,
            product_id: this.product.id,
          })

          Metrics.pushEcommerce({
            actionType: ECOMMERCE_ACTION_TYPE.detail,
            products: [this.product],
          })

          RR.reportProductView(this.product.id)
        }

        this.$store.dispatch('productPage/getSuggestedGoods', product).catch(console.error)
      } catch (e) {
        this.notFound = true
        eventBus.$emit('error', e)
      } finally {
        this.isLoading = false
      }
    },
    extractProductId(slug) {
      const parts = slug.split('-')
      const id = parts[parts.length - 1]

      return parseInt(id, 10)
    },
    updateBuyBlockOffset() {
      if (this.$refs.buyBlock) {
        this.buyBlockOffset = this.$refs.buyBlock.offsetTop
      }
    },
    setupPriceBlockPinning() {
      this.updateBuyBlockOffset()
      window.addEventListener('scroll', this.handleScroll)
      window.addEventListener('resize', this.updateBuyBlockOffset)
    },
    handleScroll() {
      if (!this.inResponsiveSizes(['xs', 'sm'])) {
        this.pinBuyBlock = false
        return
      }

      if (this.$refs.buyBlock) {
        const scrollY = window.scrollY || window.pageYOffset
        this.pinBuyBlock = scrollY > this.buyBlockOffset + this.$refs.buyBlock.getBoundingClientRect().height
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/common-imports.scss';
@import '@/assets/scss/blocks/product-page-recommendations.scss';

.product-page {
  --container-padding: 16px;
  $block-margin-bottom: 12px;
  $block-margin-bottom-small: 10px;

  color: $Dark100;

  &__container {
    position: relative;
    min-width: 280px;
    max-width: calc(1230px + var(--container-padding) * 2);
    margin: 0 auto;
    min-height: 100%;
    padding: var(--container-padding);
    display: grid;
    grid-template-columns: 1fr;
    align-content: flex-start;
    align-items: flex-start;

    @include media-breakpoint-up(md) {
      --container-padding: 24px;

      grid-template-columns: 34.2% 1fr;
      grid-auto-rows: auto;
      column-gap: 10px;
    }

    @include media-breakpoint-up(xl) {
      --container-padding: 36px;
      grid-template-columns: 43.7% 1fr;
      column-gap: 36px;
    }
  }

  &__field {
    grid-column: 1 / -1;
    margin-bottom: 20px;

    @include media-breakpoint-up(md) {
      margin-bottom: 24px;
    }
  }

  &__breadcrumbs {
    margin-bottom: 20px;
    grid-column: 1 / -1;

    @include media-breakpoint-up(md) {
      margin-bottom: 24px;
    }
  }

  &__right {
    display: grid;
    grid-template-columns: 42.5% 55.2%;
    column-gap: 10px;

    @include media-breakpoint-up(xl) {
      grid-template-columns: 38.4% 56.2%;
      column-gap: 36px;
    }
  }

  &__column {
    &--1 {
      overflow: hidden;
    }
  }

  &__meta {
    margin-bottom: $block-margin-bottom;

    @include media-breakpoint-up(xl) {
      margin-bottom: 28px;
    }
  }

  &__gallery {
    margin-bottom: $block-margin-bottom;

    @include media-breakpoint-up(md) {
      margin-bottom: 2px;
    }
  }

  &__buy {
    margin-bottom: $block-margin-bottom-small;

    @include media-breakpoint-up(xl) {
      margin-bottom: 32px;
    }
  }

  &__share {
    margin-bottom: $block-margin-bottom;

    @include media-breakpoint-up(md) {
      margin-bottom: 10px;
    }

    @include media-breakpoint-up(xl) {
      margin-bottom: 8px;
    }
  }

  &__seller {
    margin-bottom: $block-margin-bottom;

    @include media-breakpoint-up(md) {
      margin-bottom: 2px;
    }
  }

  &__delivery {
    margin-bottom: $block-margin-bottom;

    @include media-breakpoint-up(md) {
      margin-bottom: 0;
    }
  }

  &__specs {
    margin-bottom: $block-margin-bottom;

    @include media-breakpoint-up(xl) {
      margin-bottom: 28px;
    }
  }

  &__tabs {
    margin-bottom: $block-margin-bottom;

    @include media-breakpoint-up(md) {
      margin-bottom: 0;
    }
  }

  &__suggested-goods {
    grid-column: 1 / -1;
  }

  &__similar-goods {
    @include media-breakpoint-up(md) {
      grid-column: 1 / -1;
    }
  }

  &__category-goods {
    @include media-breakpoint-up(md) {
      grid-column: 1 / -1;
    }
  }
}
</style>
